import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import * as Sentry from '@sentry/angular';

import { Environment } from '@interfaces/environment';
import { User } from '@models/user.model';

import { AppConfigService } from './app-config.service';

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  private readonly appConfigService = inject(AppConfigService);

  get env(): Environment {
    return this.appConfigService.environment;
  }

  get version(): string {
    return this.appConfigService.version;
  }

  captureException(error: Error & { originalError?: Error }): void {
    if (this.env?.SENTRY_DSN) {
      Sentry.captureException(error.originalError || error);
    } else {
      console.error(error);
    }
  }

  cleanUser(): void {
    if (this.env.SENTRY_DSN) {
      Sentry.getCurrentScope()?.setUser(null);
    }
  }

  init(): void {
    if (this.env.SENTRY_DSN) {
      Sentry.init({
        integrations: [
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
            networkDetailAllowUrls: [window.location.origin]
          }),
          Sentry.browserTracingIntegration({
            beforeStartSpan(context) {
              return {
                ...context,
                name: location.pathname
                  .replace(/\?.*/g, '?[queryParams]') // On remplace les queryParams (/module?myQuery=param -> /module?[queryParams])
                  .replace(/\/\d+/g, '/{id}') // On remplace les ids (/company/1234/module -> /company/{id}/module)
              };
            }
          })
        ],
        tracePropagationTargets: [this.env.API_URL],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        dsn: this.env.SENTRY_DSN,
        environment: this.env.ENV_NAME,
        release: this.version,
        ignoreErrors: ['Non-Error exception captured', 'ChunkLoadError'],
        beforeBreadcrumb: (breadcrumb, hint) => {
          if (breadcrumb.category === 'xhr') {
            const data = {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, no-underscore-dangle
              requestBody: hint?.xhr?.__sentry_xhr__?.body,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              response: hint.xhr.response,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              responseUrl: hint.xhr.responseURL
            };
            return { ...breadcrumb, data };
          }
          return breadcrumb;
        },
        beforeSend(event, hint) {
          const error = hint.originalException;
          if (error && error instanceof HttpErrorResponse) {
            return null;
          }
          return event;
        }
      });
    }
  }

  addBreadcrumb(message: string, data: any = undefined, category: string = 'User action'): void {
    if (!this.env.SENTRY_DSN) {
      return;
    }
    Sentry.addBreadcrumb({
      message,
      data,
      category,
      level: 'info'
    });
  }

  setUser(user: User): void {
    if (this.env.SENTRY_DSN) {
      Sentry.getCurrentScope()?.setUser({
        userId: user.id,
        email: user.email,
        phone: user.phone,
        firstname: user.firstname,
        lastname: user.lastname
      });
    }
  }
}
